/**
 * Auth Store
 *
 * Zustand store for authentication state management
 * Uses IN-MEMORY storage only - session managed by Better Auth httpOnly cookies
 *
 * SECURITY: No localStorage persistence to prevent XSS token theft
 */

import { create } from 'zustand';
import type { User } from '@/shared/types/auth.types';

interface AuthStore {
  user: User | null;
  isAuthenticated: boolean;
  _sessionInitialized: boolean;
  setUser: (user: User | null) => void;
  clearUser: () => void;
  setSessionInitialized: (initialized: boolean) => void;
}

/**
 * Auth Store - In-Memory Only
 *
 * Session state is:
 * 1. Managed by Better Auth via httpOnly cookies (secure, XSS-proof)
 * 2. Synced to this store on app load via useSessionCheck hook
 * 3. Cleared on logout or session expiry
 * 4. NOT persisted to localStorage (security best practice)
 *
 * On page refresh:
 * - Store resets to null
 * - useSessionCheck automatically fetches session from Better Auth
 * - User data restored from server (cookie-based auth)
 */
export const useAuthStore = create<AuthStore>()((set) => ({
  user: null,
  isAuthenticated: false,
  _sessionInitialized: false,
  setUser: (user) =>
    set({
      user,
      isAuthenticated: !!user,
    }),
  clearUser: () =>
    set({
      user: null,
      isAuthenticated: false,
    }),
  setSessionInitialized: (initialized) =>
    set({
      _sessionInitialized: initialized,
    }),
}));
